import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Edit as EditIcon, Inventory2 as RecountIcon } from '@mui/icons-material';
import QuantityField from '../../components/QuantityField';
import { AssignLotPayload, inventoryService, InventoryLot, LotContainerType, UpdateLotPayload } from '../../services/inventoryService';
import { useClinicTimezone } from '../../hooks/useClinicTimezone';
import { calendarDaysUntil, formatCalendarDate } from '../../utils/dateUtils';
import { daysOpen, isPastInUseWindow, lotExpiry } from '../../components/Vaccination/fefo';
import { describeBalance } from '../../components/Vaccination/lotDraw';

interface InventoryLotsDialogProps {
  item: { id: string; name: string } | null;
  onClose: () => void;
  canEdit: boolean;
  canRecount: boolean;
  canAddDetails: boolean;
}

const PACKAGING: Record<LotContainerType, string> = { MULTI_DOSE: 'Multi-dose bottle', SINGLE_DOSE: 'Single-dose vial' };
const containers = (n: number) => `${n} ${n === 1 ? 'container' : 'containers'}`;

/**
 * An item's lots (ADR-0047): what is left, when it expires, how long the open bottle has been open.
 * Details are corrected here; the mL left changes only through a recount, which is ledgered with its reason.
 */
const InventoryLotsDialog: React.FC<InventoryLotsDialogProps> = ({ item, onClose, canEdit, canRecount, canAddDetails }) => {
  const tz = useClinicTimezone();
  // A phone gets the whole screen, so the lot actions are not scrolled off the table's right edge.
  const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [editing, setEditing] = useState<InventoryLot | null>(null);
  const [recounting, setRecounting] = useState<InventoryLot | null>(null);
  const [addingDetails, setAddingDetails] = useState(false);

  const { data: lots = [], isLoading } = useQuery(['inventory', item?.id, 'lots'], () => inventoryService.getItemLots(item!.id), {
    enabled: !!item,
    retry: false,
  });
  // Stock received without a lot (or from before lots) that the lots below do not account for.
  const { data: coverage } = useQuery(['inventory', item?.id, 'lot-coverage'], () => inventoryService.getLotCoverage(item!.id), {
    enabled: !!item,
    retry: false,
  });
  const unassigned = coverage?.unassignedContainers ?? 0;
  const refresh = () => {
    queryClient.invalidateQueries(['inventory']);
    queryClient.invalidateQueries(['vaccine-lots']);
  };

  const retire = useMutation((lot: InventoryLot) => inventoryService.updateLot(lot.id, { isArchived: !lot.isArchived }), {
    onSuccess: refresh,
    onError: (e: any) => void enqueueSnackbar(e?.response?.data?.message || 'Could not update the lot', { variant: 'error' }),
  });

  return (
    <Dialog open={!!item} onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen}>
      <DialogTitle>Lots and expiry dates{item ? `: ${item.name}` : ''}</DialogTitle>
      <DialogContent>
        {unassigned > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              canAddDetails ? (
                <Button color="inherit" size="small" onClick={() => setAddingDetails(true)}>
                  Add lot details
                </Button>
              ) : undefined
            }
          >
            {containers(unassigned)} in stock {unassigned === 1 ? 'has' : 'have'} no lot details. Adding them keeps the
            amount on the shelf the same.
          </Alert>
        )}
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={28} />
          </Box>
        ) : lots.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No lots recorded. Lots are added when stock is received with a lot number.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lot</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Left</TableCell>
                  <TableCell>Packaging</TableCell>
                  <TableCell>Open bottle</TableCell>
                  <TableCell>Manufacturer</TableCell>
                  {(canEdit || canRecount) && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {lots.map((lot) => {
                  const expiry = lotExpiry(lot, tz);
                  const days = calendarDaysUntil(lot.expiresOn, tz);
                  const open = daysOpen(lot, tz);
                  return (
                    <TableRow key={lot.id} sx={{ opacity: lot.isArchived ? 0.55 : 1 }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {lot.lotNumber}
                        </Typography>
                        {lot.isArchived && <Chip size="small" variant="outlined" label="Retired" sx={{ mt: 0.5 }} />}
                      </TableCell>
                      <TableCell>
                        {formatCalendarDate(lot.expiresOn, 'MMM d, yyyy')}{' '}
                        {expiry === 'expired' && <Chip size="small" color="error" label="Expired" />}
                        {expiry === 'expiring' && days !== null && <Chip size="small" color="warning" label={`${days}d left`} />}
                      </TableCell>
                      <TableCell>
                        {describeBalance(lot)}
                        {lot.mlRemaining < 0 && <Chip size="small" color="error" variant="outlined" label="Overdrawn" sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell>
                        {PACKAGING[lot.containerType]}, {lot.mlPerContainer} mL
                      </TableCell>
                      <TableCell>
                        {open === null ? (
                          '-'
                        ) : (
                          <Chip
                            size="small"
                            variant="outlined"
                            color={isPastInUseWindow(lot, tz) ? 'error' : 'default'}
                            label={lot.inUseDays != null ? `${open}d of ${lot.inUseDays}d` : `${open}d`}
                          />
                        )}
                      </TableCell>
                      <TableCell>{lot.manufacturer || '-'}</TableCell>
                      {(canEdit || canRecount) && (
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {canRecount && (
                            <IconButton size="small" title="Recount" aria-label={`Recount lot ${lot.lotNumber}`} onClick={() => setRecounting(lot)}>
                              <RecountIcon fontSize="small" />
                            </IconButton>
                          )}
                          {canEdit && (
                            <>
                              <IconButton size="small" title="Edit" aria-label={`Edit lot ${lot.lotNumber}`} onClick={() => setEditing(lot)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <Button size="small" onClick={() => retire.mutate(lot)} disabled={retire.isLoading}>
                                {lot.isArchived ? 'Restore' : 'Retire'}
                              </Button>
                            </>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <EditLotDialog lot={editing} onClose={() => setEditing(null)} onSaved={refresh} />
      <RecountLotDialog lot={recounting} onClose={() => setRecounting(null)} onSaved={refresh} />
      {item && addingDetails && (
        <AddLotDetailsDialog
          itemId={item.id}
          unassigned={unassigned}
          lots={lots}
          onClose={() => setAddingDetails(false)}
          onSaved={refresh}
        />
      )}
    </Dialog>
  );
};

/** Correct a lot's number, expiry, packaging, window or manufacturer. A packaging fix keeps the count. */
const EditLotDialog: React.FC<{ lot: InventoryLot | null; onClose: () => void; onSaved: () => void }> = ({ lot, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [draft, setDraft] = useState<UpdateLotPayload>({});
  React.useEffect(() => {
    if (lot) {
      setDraft({
        lotNumber: lot.lotNumber,
        expiresOn: lot.expiresOn.slice(0, 10),
        containerType: lot.containerType,
        mlPerContainer: lot.mlPerContainer,
        manufacturer: lot.manufacturer ?? '',
        inUseDays: lot.inUseDays,
      });
    }
  }, [lot]);
  const save = useMutation(
    () =>
      inventoryService.updateLot(lot!.id, {
        ...draft,
        manufacturer: draft.manufacturer?.trim() || null,
      }),
    {
      onSuccess: () => {
        onSaved();
        enqueueSnackbar('Lot updated', { variant: 'success' });
        onClose();
      },
      onError: (e: any) => void enqueueSnackbar(e?.response?.data?.message || 'Could not update the lot', { variant: 'error' }),
    },
  );
  const valid = !!draft.lotNumber?.trim() && !!draft.expiresOn && !!draft.mlPerContainer && draft.mlPerContainer > 0;

  return (
    <Dialog open={!!lot} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit lot {lot?.lotNumber}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField label="Lot number" size="small" fullWidth value={draft.lotNumber ?? ''} onChange={(e) => setDraft({ ...draft, lotNumber: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Expiration date"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={draft.expiresOn ?? ''}
              onChange={(e) => setDraft({ ...draft, expiresOn: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Packaging"
              size="small"
              fullWidth
              value={draft.containerType ?? 'MULTI_DOSE'}
              onChange={(e) => setDraft({ ...draft, containerType: e.target.value as LotContainerType })}
            >
              <MenuItem value="MULTI_DOSE">{PACKAGING.MULTI_DOSE}</MenuItem>
              <MenuItem value="SINGLE_DOSE">{PACKAGING.SINGLE_DOSE}</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <QuantityField
              label="mL per container"
              size="small"
              fullWidth
              value={draft.mlPerContainer ?? null}
              onChange={(next) => setDraft({ ...draft, mlPerContainer: next ?? undefined })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Manufacturer" size="small" fullWidth value={draft.manufacturer ?? ''} onChange={(e) => setDraft({ ...draft, manufacturer: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Days usable once opened"
              type="number"
              size="small"
              fullWidth
              value={draft.inUseDays ?? ''}
              onChange={(e) => setDraft({ ...draft, inUseDays: e.target.value ? Math.max(1, Math.round(Number(e.target.value))) : null })}
              helperText="Leave blank if not known"
            />
          </Grid>
        </Grid>
        {lot && draft.mlPerContainer !== undefined && draft.mlPerContainer !== lot.mlPerContainer && (
          <Alert severity="info" sx={{ mt: 2 }}>
            The number of containers stays the same and the mL left is worked out again from the new size.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={!valid || save.isLoading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Lot details for containers already counted without them. Mounted per opening, so it is seeded once:
 * packaging starts from the item's most recent lot, as on Receive, and the idempotency key makes a double
 * click replay. A lot number the item already has tops that lot up with its own expiry and packaging.
 */
const AddLotDetailsDialog: React.FC<{
  itemId: string;
  unassigned: number;
  lots: InventoryLot[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ itemId, unassigned, lots, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const lastLot = [...lots].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  const [draft, setDraft] = useState<AssignLotPayload>(() => ({
    lotNumber: '',
    expiresOn: '',
    containers: unassigned,
    containerType: lastLot?.containerType ?? 'MULTI_DOSE',
    mlPerContainer: lastLot?.mlPerContainer,
    manufacturer: lastLot?.manufacturer ?? '',
    inUseDays: lastLot?.inUseDays ?? undefined,
  }));
  const [key] = useState(() =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${itemId}`,
  );
  const typed = draft.lotNumber.trim().replace(/\s+/g, ' ').toUpperCase();
  const known = typed ? lots.find((l) => l.lotNumber === typed) : undefined;
  const save = useMutation(
    () =>
      inventoryService.assignLot(itemId, {
        ...draft,
        ...(known
          ? { expiresOn: known.expiresOn.slice(0, 10), containerType: known.containerType, mlPerContainer: known.mlPerContainer }
          : {}),
        lotNumber: draft.lotNumber.trim(),
        manufacturer: draft.manufacturer?.trim() || undefined,
        inUseDays: draft.containerType === 'MULTI_DOSE' ? draft.inUseDays : undefined,
        idempotencyKey: key,
      }),
    {
      onSuccess: (res) => {
        onSaved();
        enqueueSnackbar(res.applied ? `Lot details added to ${containers(res.assignedContainers)}` : 'Already added', {
          variant: res.applied ? 'success' : 'info',
        });
        onClose();
      },
      onError: (e: any) => void enqueueSnackbar(e?.response?.data?.message || 'Could not add the lot details', { variant: 'error' }),
    },
  );
  const count = draft.containers ?? 0;
  const single = (known?.containerType ?? draft.containerType) === 'SINGLE_DOSE';
  const countOk = count > 0 && count <= unassigned && (!single || Number.isInteger(count));
  const valid =
    !!typed && countOk && (!!known || (!!draft.expiresOn && !!draft.mlPerContainer && draft.mlPerContainer > 0));

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add lot details</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {containers(unassigned)} {unassigned === 1 ? 'has' : 'have'} no lot details. Type in what is printed on the box.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Lot number"
              size="small"
              fullWidth
              required
              autoFocus
              value={draft.lotNumber}
              onChange={(e) => setDraft({ ...draft, lotNumber: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Expiration date"
              type="date"
              size="small"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={known ? known.expiresOn.slice(0, 10) : draft.expiresOn}
              disabled={!!known}
              onChange={(e) => setDraft({ ...draft, expiresOn: e.target.value })}
            />
          </Grid>
          {known && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Adds to lot {known.lotNumber}, already on this item, with its expiry and packaging.
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <QuantityField
              label="Containers with this lot"
              size="small"
              fullWidth
              value={draft.containers ?? null}
              onChange={(next) => setDraft({ ...draft, containers: next ?? undefined })}
            />
            <Typography variant="caption" color={countOk ? 'text.secondary' : 'error'} display="block" sx={{ mt: 0.5 }}>
              {single && !Number.isInteger(count)
                ? 'Single-dose vials are labelled whole.'
                : `Up to ${unassigned}. Use fewer if the delivery had more than one lot.`}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Packaging"
              size="small"
              fullWidth
              value={known?.containerType ?? draft.containerType ?? 'MULTI_DOSE'}
              disabled={!!known}
              onChange={(e) => setDraft({ ...draft, containerType: e.target.value as LotContainerType })}
            >
              <MenuItem value="MULTI_DOSE">{PACKAGING.MULTI_DOSE}</MenuItem>
              <MenuItem value="SINGLE_DOSE">{PACKAGING.SINGLE_DOSE}</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <QuantityField
              label="mL per container"
              size="small"
              fullWidth
              disabled={!!known}
              value={known?.mlPerContainer ?? draft.mlPerContainer ?? null}
              onChange={(next) => setDraft({ ...draft, mlPerContainer: next ?? undefined })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Manufacturer"
              size="small"
              fullWidth
              value={draft.manufacturer ?? ''}
              onChange={(e) => setDraft({ ...draft, manufacturer: e.target.value })}
            />
          </Grid>
          {!single && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Days usable once opened"
                type="number"
                size="small"
                fullWidth
                value={draft.inUseDays ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, inUseDays: e.target.value ? Math.max(1, Math.round(Number(e.target.value))) : undefined })
                }
                helperText="Leave blank if not known"
              />
            </Grid>
          )}
        </Grid>
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          The amount on the shelf does not change. The stock history records who added the lot details.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={!valid || save.isLoading}>
          Add lot details
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** A shelf count of one lot: full containers plus what is left in the open bottle, with a reason. */
const RecountLotDialog: React.FC<{ lot: InventoryLot | null; onClose: () => void; onSaved: () => void }> = ({ lot, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [full, setFull] = useState<number | null>(null);
  const [openMl, setOpenMl] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  React.useEffect(() => {
    setFull(null);
    setOpenMl(null);
    setReason('');
  }, [lot]);
  const counted = lot ? Math.round(((full ?? 0) * lot.mlPerContainer + (lot.containerType === 'MULTI_DOSE' ? openMl ?? 0 : 0)) * 1000) / 1000 : 0;
  const save = useMutation(() => inventoryService.adjustLot(lot!.id, { countedMl: counted, reason: reason.trim() }), {
    onSuccess: () => {
      onSaved();
      enqueueSnackbar('Lot recounted', { variant: 'success' });
      onClose();
    },
    onError: (e: any) => void enqueueSnackbar(e?.response?.data?.message || 'Could not recount the lot', { variant: 'error' }),
  });
  const single = lot?.containerType === 'SINGLE_DOSE';

  return (
    <Dialog open={!!lot} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Recount lot {lot?.lotNumber}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Recorded now: {lot ? describeBalance(lot) : ''}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={single ? 12 : 6}>
            <QuantityField label={single ? 'Vials on the shelf' : 'Full bottles'} size="small" fullWidth value={full} onChange={setFull} />
          </Grid>
          {!single && (
            <Grid item xs={6}>
              <QuantityField label="mL in the open bottle" size="small" fullWidth value={openMl} onChange={setOpenMl} />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              label="Reason"
              size="small"
              fullWidth
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Shelf count, bottle discarded"
            />
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
          Counted: {counted} mL. On-hand moves to match.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={!reason.trim() || full === null || save.isLoading}>
          Save count
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryLotsDialog;
